// React Query hooks for /exhibits/*. Built on top of the typed wrappers in
// shared/src/api/exhibits.ts so consumers (StaffDashboard in PR 10, the rest
// in PR 11+) can drop axios calls and get loading / error / cache for free.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AssetKind,
  CodeRegenerationResponse,
  Exhibit,
  ExhibitCreate,
  ExhibitListItem,
  ExhibitTransitionRequest,
  ExhibitUpdate,
  ImageUploadResponse,
  QrGenerationResponse,
  SetNarrationRequest,
  assignExhibitZone,
  createExhibit,
  deleteExhibit,
  generateExhibitNarration,
  generateExhibitQR,
  getStaffExhibit,
  listExhibits,
  regenerateExhibitCode,
  removeExhibitMedia,
  setExhibitNarration,
  transitionExhibit,
  updateExhibit,
  updateExhibitMedia,
  uploadExhibitImage,
  uploadExhibitMedia,
  uploadExhibitNarration,
} from "../exhibits";
import type { ListStatus } from "../listStatus";
import { zoneKeys } from "./useZones";

// Keys — kept colocated so cache-invalidation reads obvious. The list key is
// a tuple so future filters (state, owner, search) can extend without renaming.
export const exhibitKeys = {
  all: ["exhibits"] as const,
  list: () => [...exhibitKeys.all, "list"] as const,
  detail: (id: string) => [...exhibitKeys.all, "detail", id] as const,
};

export function useExhibitList(status?: ListStatus) {
  return useQuery<ExhibitListItem[]>({
    queryKey: [...exhibitKeys.list(), { status: status ?? null }],
    queryFn: () => listExhibits(status),
  });
}

export function useExhibit(exhibitId: string | undefined) {
  // F-C: hits the STAFF endpoint (returns the row regardless of
  // state). The visitor `/exhibits/{id}` 404s on non-published,
  // which broke ExhibitForm + PublishToggle on cascade-archived
  // rows (empty fields, no lockdown banner).
  return useQuery<Exhibit>({
    queryKey: exhibitKeys.detail(exhibitId ?? ""),
    queryFn: () => getStaffExhibit(exhibitId as string),
    enabled: Boolean(exhibitId),
  });
}

export function useCreateExhibit() {
  const qc = useQueryClient();
  return useMutation<Exhibit, Error, ExhibitCreate>({
    mutationFn: createExhibit,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: exhibitKeys.list() });
      qc.setQueryData(exhibitKeys.detail(created.id), created);
    },
  });
}

export function useUpdateExhibit() {
  const qc = useQueryClient();
  return useMutation<
    Exhibit,
    Error,
    { exhibitId: string; payload: ExhibitUpdate }
  >({
    mutationFn: ({ exhibitId, payload }) => updateExhibit(exhibitId, payload),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: exhibitKeys.list() });
      qc.setQueryData(exhibitKeys.detail(updated.id), updated);
    },
  });
}

// Moving an exhibit rewrites `exhibit_ids` on up to two ZONES, so the zone
// caches are what actually go stale — invalidate them wholesale alongside
// the exhibit list (whose rows carry the zone_id annotation).
export function useAssignExhibitZone() {
  const qc = useQueryClient();
  return useMutation<
    Exhibit,
    Error,
    { exhibitId: string; zoneId: string | null }
  >({
    mutationFn: ({ exhibitId, zoneId }) => assignExhibitZone(exhibitId, zoneId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: exhibitKeys.list() });
      qc.invalidateQueries({ queryKey: zoneKeys.all });
    },
  });
}

export function useTransitionExhibit() {
  const qc = useQueryClient();
  return useMutation<
    Exhibit,
    Error,
    { exhibitId: string; payload: ExhibitTransitionRequest }
  >({
    mutationFn: ({ exhibitId, payload }) =>
      transitionExhibit(exhibitId, payload),
    onSuccess: (transitioned) => {
      qc.invalidateQueries({ queryKey: exhibitKeys.list() });
      qc.setQueryData(exhibitKeys.detail(transitioned.id), transitioned);
    },
  });
}

export function useDeleteExhibit() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: deleteExhibit,
    onSuccess: (_, exhibitId) => {
      qc.invalidateQueries({ queryKey: exhibitKeys.list() });
      qc.removeQueries({ queryKey: exhibitKeys.detail(exhibitId) });
    },
  });
}

export function useUploadExhibitImage() {
  const qc = useQueryClient();
  return useMutation<
    ImageUploadResponse,
    Error,
    { exhibitId: string; file: File }
  >({
    mutationFn: ({ exhibitId, file }) => uploadExhibitImage(exhibitId, file),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: exhibitKeys.list() });
      qc.invalidateQueries({
        queryKey: exhibitKeys.detail(variables.exhibitId),
      });
    },
  });
}

// ── F-F media + narration ───────────────────────────────────────────────
// Each mutation returns the updated Exhibit, so we prime the detail cache
// (instant form refresh) and invalidate the list (mirrors useUpdateExhibit).

export function useSetExhibitNarration() {
  const qc = useQueryClient();
  return useMutation<
    Exhibit,
    Error,
    { exhibitId: string; language: string; payload: SetNarrationRequest }
  >({
    mutationFn: ({ exhibitId, language, payload }) =>
      setExhibitNarration(exhibitId, language, payload),
    onSuccess: (updated) => {
      qc.setQueryData(exhibitKeys.detail(updated.id), updated);
      qc.invalidateQueries({ queryKey: exhibitKeys.list() });
    },
  });
}

export function useGenerateExhibitNarration() {
  const qc = useQueryClient();
  return useMutation<Exhibit, Error, { exhibitId: string; language: string }>({
    mutationFn: ({ exhibitId, language }) =>
      generateExhibitNarration(exhibitId, language),
    onSuccess: (updated) => {
      qc.setQueryData(exhibitKeys.detail(updated.id), updated);
      qc.invalidateQueries({ queryKey: exhibitKeys.list() });
    },
  });
}

export function useUploadExhibitNarration() {
  const qc = useQueryClient();
  return useMutation<
    Exhibit,
    Error,
    { exhibitId: string; language: string; file: File }
  >({
    mutationFn: ({ exhibitId, language, file }) =>
      uploadExhibitNarration(exhibitId, language, file),
    onSuccess: (updated) => {
      qc.setQueryData(exhibitKeys.detail(updated.id), updated);
      qc.invalidateQueries({ queryKey: exhibitKeys.list() });
    },
  });
}

export function useUploadExhibitMedia() {
  const qc = useQueryClient();
  return useMutation<
    Exhibit,
    Error,
    {
      exhibitId: string;
      file: File;
      kind: AssetKind;
      language?: string | null;
      caption?: string | null;
    }
  >({
    mutationFn: ({ exhibitId, ...params }) =>
      uploadExhibitMedia(exhibitId, params),
    onSuccess: (updated) => {
      qc.setQueryData(exhibitKeys.detail(updated.id), updated);
      qc.invalidateQueries({ queryKey: exhibitKeys.list() });
    },
  });
}

export function useUpdateExhibitMedia() {
  const qc = useQueryClient();
  return useMutation<
    Exhibit,
    Error,
    {
      exhibitId: string;
      assetId: string;
      caption: string | null;
      language: string | null;
    }
  >({
    mutationFn: ({ exhibitId, assetId, caption, language }) =>
      updateExhibitMedia(exhibitId, assetId, { caption, language }),
    onSuccess: (updated) => {
      qc.setQueryData(exhibitKeys.detail(updated.id), updated);
      qc.invalidateQueries({ queryKey: exhibitKeys.list() });
    },
  });
}

export function useRemoveExhibitMedia() {
  const qc = useQueryClient();
  return useMutation<Exhibit, Error, { exhibitId: string; assetId: string }>({
    mutationFn: ({ exhibitId, assetId }) =>
      removeExhibitMedia(exhibitId, assetId),
    onSuccess: (updated) => {
      qc.setQueryData(exhibitKeys.detail(updated.id), updated);
      qc.invalidateQueries({ queryKey: exhibitKeys.list() });
    },
  });
}

export function useGenerateExhibitQR() {
  const qc = useQueryClient();
  return useMutation<QrGenerationResponse, Error, string>({
    mutationFn: generateExhibitQR,
    onSuccess: (_, exhibitId) => {
      qc.invalidateQueries({ queryKey: exhibitKeys.list() });
      qc.invalidateQueries({ queryKey: exhibitKeys.detail(exhibitId) });
    },
  });
}

export function useRegenerateExhibitCode() {
  const qc = useQueryClient();
  return useMutation<CodeRegenerationResponse, Error, string>({
    mutationFn: regenerateExhibitCode,
    onSuccess: (_, exhibitId) => {
      qc.invalidateQueries({ queryKey: exhibitKeys.list() });
      qc.invalidateQueries({ queryKey: exhibitKeys.detail(exhibitId) });
    },
  });
}
